import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, HeartStraight, Star, Quotes, CaretRight, HandHeart, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import SectionTitle from "@/components/SectionTitle";
import { getServices, getDoctors, getTestimonials, getBlogs } from "@/lib/api";

const STATS = [
  { value: "25+", label: "Years of care" },
  { value: "180+", label: "Specialist doctors" },
  { value: "40k+", label: "Lives touched yearly" },
  { value: "24/7", label: "Emergency response" },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    getServices().then(setServices).catch(() => {});
    getDoctors().then(setDoctors).catch(() => {});
    getTestimonials().then(setTestimonials).catch(() => {});
    getBlogs().then(setBlogs).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-brand-sage mb-6">
              <Sparkle size={14} weight="fill" /> A hospital that feels human
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-brand-ink">
              Modern medicine,<br />
              <em className="text-brand-sage not-italic font-serif italic">gentle</em> hands.
            </h1>
            <p className="mt-7 text-lg text-brand-muted max-w-xl leading-relaxed">
              Jashoda Hospital blends world-class specialists with a calmer, kinder approach to healing.
              Book your visit in under a minute — no queues, no waiting rooms full of stress.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/book">
                <Button
                  data-testid="hero-book-btn"
                  className="rounded-full bg-[#C85A40] hover:bg-[#A94A32] text-white h-14 px-8 text-base transition-transform hover:scale-[1.03]"
                >
                  Book an Appointment <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  variant="outline"
                  data-testid="hero-services-btn"
                  className="rounded-full h-14 px-8 text-base border-brand-ink/20 hover:bg-brand-ink hover:text-brand-bone"
                >
                  Explore Services
                </Button>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-serif text-3xl sm:text-4xl text-brand-ink">{s.value}</div>
                  <div className="text-xs text-brand-muted mt-1 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 fade-up delay-2 relative">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#E8ECE4]" />
              <div className="absolute -bottom-8 -right-4 w-40 h-40 rounded-full bg-[#F0E4DE]" />
              <img
                src="https://images.unsplash.com/photo-1584516150909-c43483ee7932?w=1200&q=85"
                alt="Doctor consulting patient with care"
                className="relative rounded-[2rem] w-full h-[520px] lg:h-[620px] object-cover border border-brand-border"
              />
              <div className="absolute bottom-6 left-6 right-6 lg:left-8 lg:right-auto lg:w-72 bg-brand-bone/95 backdrop-blur-md rounded-2xl p-5 border border-brand-border shadow-[0_20px_50px_rgba(26,36,33,0.12)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center">
                    <HandHeart size={20} weight="fill" className="text-brand-bone" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-brand-muted">Patient satisfaction</div>
                    <div className="font-serif text-2xl text-brand-ink leading-tight">98.4% rating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-brand-border bg-[#F0EEE6] py-8" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-around gap-6 text-brand-muted">
          <div className="flex items-center gap-2 text-sm"><ShieldCheck size={20} className="text-brand-sage" /> NABH Accredited</div>
          <div className="flex items-center gap-2 text-sm"><Clock size={20} className="text-brand-sage" /> Under-8-min ambulance dispatch</div>
          <div className="flex items-center gap-2 text-sm"><HeartStraight size={20} className="text-brand-sage" weight="fill" /> Family-centered care</div>
          <div className="flex items-center gap-2 text-sm"><Star size={20} className="text-brand-sage" weight="fill" /> 4.9 / 5 (12,400 reviews)</div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="services-section">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] text-brand-sage mb-4">What we treat</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-brand-ink">
              Specialities under one calm roof.
            </h2>
          </div>
          <Link to="/services" data-testid="all-services-link" className="text-brand-ink font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
            All departments <CaretRight size={18} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {services.slice(0, 8).map((s, i) => (
            <Link
              key={s.id}
              to={`/services/${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className={`group relative rounded-3xl p-7 border border-brand-border transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(26,36,33,0.08)] overflow-hidden ${
                i === 0 ? "md:col-span-2 md:row-span-2 min-h-[380px]" : "min-h-[220px]"
              }`}
              style={{ backgroundColor: s.accent }}
            >
              {i === 0 && (
                <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity" />
              )}
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-3">{s.short_description}</div>
                  <div className={`font-serif tracking-tight text-brand-ink ${i === 0 ? "text-4xl sm:text-5xl" : "text-2xl"}`}>
                    {s.name}
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-brand-sage text-sm font-medium">
                  Learn more <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DOCTORS */}
      <section className="bg-[#F0EEE6] py-24 border-y border-brand-border" data-testid="doctors-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.28em] text-brand-sage mb-4">Meet the humans</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                Doctors who listen first, prescribe second.
              </h2>
            </div>
            <Link to="/doctors" data-testid="all-doctors-link" className="text-brand-ink font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
              See all doctors <CaretRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {doctors.slice(0, 4).map((d) => (
              <Link
                key={d.id}
                to={`/doctors/${d.id}`}
                data-testid={`doctor-card-${d.id}`}
                className="group bg-white rounded-3xl overflow-hidden border border-brand-border hover:-translate-y-1 transition-transform"
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#E8ECE4]">
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wider text-brand-sage mb-1.5">{d.specialty}</div>
                  <div className="font-serif text-xl text-brand-ink leading-tight">{d.name}</div>
                  <div className="text-sm text-brand-muted mt-2">{d.experience_years} years experience</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="testimonials-section">
        <SectionTitle
          eyebrow="Stories of healing"
          title="Real people. Real moments."
          description="Every hallway in this hospital carries a story. Here are a few of them."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.id}
              data-testid={`testimonial-${i}`}
              className={`relative bg-white border border-brand-border rounded-3xl p-8 md:p-10 ${i % 2 === 1 ? "md:mt-12" : ""}`}
            >
              <Quotes size={40} weight="fill" className="text-[#E8ECE4] absolute top-6 right-6" />
              <p className="font-serif italic text-2xl leading-snug text-brand-ink relative z-10">
                "{t.quote}"
              </p>
              <div className="mt-8 flex items-center gap-4">
                <img src={t.image} alt={t.patient_name} className="w-12 h-12 rounded-full object-cover" />
                <div>
                  <div className="font-medium text-brand-ink">{t.patient_name}</div>
                  <div className="text-xs text-brand-muted uppercase tracking-wider">{t.role}</div>
                </div>
                <div className="ml-auto flex gap-1">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} size={16} weight="fill" className="text-[#C85A40]" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="cta-strip">
        <div className="rounded-3xl bg-[#1A2421] text-brand-bone px-8 py-16 md:p-16 grid md:grid-cols-2 gap-10 items-center overflow-hidden relative">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-brand-bone/60 mb-4">Ready when you are</div>
            <h3 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
              Book your visit in <em className="italic text-[#C85A40] not-italic">under a minute.</em>
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
            <Link to="/book">
              <Button data-testid="cta-book-btn" className="rounded-full bg-[#C85A40] hover:bg-[#A94A32] text-white h-14 px-8 text-base">
                Book Appointment <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" data-testid="cta-contact-btn" className="rounded-full h-14 px-8 text-base bg-transparent border-brand-bone/30 text-brand-bone hover:bg-brand-bone hover:text-brand-ink">
                Speak to us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="blog-section">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] text-brand-sage mb-4">Health journal</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Reading that keeps you well.
            </h2>
          </div>
          <Link to="/blog" data-testid="all-blogs-link" className="text-brand-ink font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
            All articles <CaretRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogs.slice(0, 3).map((b) => (
            <Link
              key={b.id}
              to={`/blog/${b.slug}`}
              data-testid={`blog-preview-${b.slug}`}
              className="group flex flex-col"
            >
              <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-[#E8ECE4] mb-5">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-sage mb-3">{b.category} · {b.read_time} min read</div>
              <h3 className="font-serif text-2xl text-brand-ink leading-tight group-hover:text-brand-sage transition-colors">
                {b.title}
              </h3>
              <p className="text-brand-muted text-sm mt-3 leading-relaxed">{b.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
